import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api.js";
import { formatDate, formatTime } from "../format.js";
import type { ActionOutcome } from "../ui/ActionConfirmation.js";
import { bookingAttemptAfterFailure, bookingFailureKind } from "./attempt.js";
import { idempotencyKey } from "./api.js";
import { BulkCancellationDecisions } from "./BulkCancellationDecisions.js";
import {
  cancellationChoicesComplete,
  cancellationCreditChoices,
  chooseAllCancellationCredits,
  cancellationDecisionRequired,
  keepValidCreditChoices,
  type CancellationChoices,
} from "./cancellationChoices.js";
import { NotifyToggle } from "./notify.js";
import {
  staffBooking,
  type BulkCancelPreview,
  type BulkCancelResult,
  type RecurrenceSeriesFromAppointment,
} from "./staff.js";

type ResultRow = BulkCancelResult["results"][number] & {
  outcome_unknown?: boolean;
};

function confirmedRow(row: ResultRow): boolean {
  const finalStatus = row.final_status?.toLowerCase();
  return row.success || finalStatus === "cancelled" || finalStatus === "canceled";
}

function hasUnknownOutcome(result: BulkCancelResult): boolean {
  const response = result as BulkCancelResult & { unknown_count?: number };
  return (response.unknown_count ?? 0) > 0 ||
    result.results.some((row) => (row as ResultRow).outcome_unknown === true);
}

function resultDetails(
  result: BulkCancelResult,
  stopped: boolean,
  stopError?: string,
): ReactNode {
  const unconfirmed = result.results.filter((row) => !confirmedRow(row));
  return (
    <>
      <p>
        {result.cancelled_count} lesson{result.cancelled_count === 1 ? "" : "s"} cancelled.
        {result.already_cancelled_count > 0 &&
          ` ${result.already_cancelled_count} were already cancelled.`}
      </p>
      <p>
        {result.credits_restored > 0
          ? `${result.credits_restored} lesson ${result.credits_restored === 1 ? "credit was" : "credits were"} returned.`
          : "No lesson credits were returned."}{" "}
        {(result.credits_granted ?? 0) > 0
          ? `${result.credits_granted} new rebooking ${result.credits_granted === 1 ? "credit was" : "credits were"} issued.`
          : "No new rebooking credits were issued."}
      </p>
      <p>
        {stopped
          ? "The recurring plan was stopped."
          : stopError
            ? `The recurring plan stop could not be confirmed: ${stopError}`
            : "The series was not stopped because some lessons were not cancelled."}
      </p>
      {result.results.length > 0 && (
        <ul className="small">
          {result.results.map((row) => {
            const effects = [
              row.credits_restored > 0 && `${row.credits_restored} credit${row.credits_restored === 1 ? "" : "s"} returned`,
              (row.credits_granted ?? 0) > 0 && `${row.credits_granted} rebooking credit${row.credits_granted === 1 ? "" : "s"} issued`,
              row.credit_resolution === "consume" && "existing credit kept used",
              row.paid_cancellation_action === "retain_payment" && "payment kept as cancellation charge",
              row.credit_review_required && "credit review required",
            ].filter(Boolean);
            return (
              <li key={row.appointment_id}>
              Lesson #{row.appointment_id}: {confirmedRow(row) ? "cancelled" : (row as ResultRow).outcome_unknown ? "needs checking" : row.message ?? "not cancelled"}
                {effects.length > 0 && ` — ${effects.join("; ")}`}
              </li>
            );
          })}
        </ul>
      )}
      {unconfirmed.length > 0 && (
        <p className="small">{unconfirmed.length} lesson{unconfirmed.length === 1 ? "" : "s"} still need attention.</p>
      )}
    </>
  );
}

function cancellationOutcome(
  result: BulkCancelResult,
  stopped: boolean,
  stopError?: string,
): ActionOutcome {
  const hasFailures = result.results.some((row) => !confirmedRow(row));
  const needsReview = result.results.some((row) => row.credit_review_required);
  const attention = hasFailures || needsReview || !stopped;
  return {
    variant: attention ? "attention" : "success",
    title: stopped
      ? "Series cancelled and stopped"
      : stopError
        ? "Lessons cancelled; series stop needs checking"
        : "Series was not stopped",
    details: [{ value: resultDetails(result, stopped, stopError) }],
  };
}

function isUncertainFailure(cause: unknown): boolean {
  return bookingFailureKind(cause) === "uncertain";
}

export function SeriesCancellation({
  series,
  onBack,
  onBusyChange,
  onUncertainChange,
  onFinished,
}: {
  series: RecurrenceSeriesFromAppointment;
  onBack: () => void;
  onBusyChange: (busy: boolean) => void;
  onUncertainChange: (uncertain: boolean) => void;
  onFinished: (outcome: ActionOutcome, changedAppointmentIds: number[]) => void;
}) {
  const appointmentIds = useMemo(
    () => series.following.map((item) => item.amelia_appointment_id),
    [series],
  );
  const key = useRef(idempotencyKey());
  const [preview, setPreview] = useState<BulkCancelPreview | null>(null);
  const [choices, setChoices] = useState<CancellationChoices>({});
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [uncertainResult, setUncertainResult] = useState<BulkCancelResult | null>(null);

  const setAttemptBusy = useCallback((nextBusy: boolean) => {
    setBusy(nextBusy);
    onBusyChange(nextBusy);
  }, [onBusyChange]);

  const look = useCallback(async () => {
    setAttemptBusy(true);
    setError(null);
    setPreview(null);
    try {
      const next = await staffBooking.bulkCancelPreview(appointmentIds, "");
      setPreview(next);
      setChoices((current) => keepValidCreditChoices(next, current));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The series cancellation preview could not be loaded.");
    } finally {
      setAttemptBusy(false);
    }
  }, [appointmentIds, setAttemptBusy]);

  useEffect(() => {
    void look();
    return () => onBusyChange(false);
  }, [look, onBusyChange]);

  /** The fresh preview, when it needs a credit choice the request did not carry. */
  async function previewNeedingUnsentChoice(sentIds: ReadonlySet<number>): Promise<BulkCancelPreview | null> {
    try {
      const fresh = await staffBooking.bulkCancelPreview(appointmentIds, "");
      return fresh.items.some((item) => cancellationDecisionRequired(item) && !sentIds.has(item.appointment_id))
        ? fresh
        : null;
    } catch {
      // The preview could not settle it, so the 409 stays uncertain.
      return null;
    }
  }

  async function confirm() {
    if (!preview || busy || !cancellationChoicesComplete(preview, choices)) return;

    setAttemptBusy(true);
    setError(null);
    const sentChoices = cancellationCreditChoices(preview, choices);
    try {
      const result = await staffBooking.bulkCancel(
        appointmentIds,
        reason.trim(),
        key.current,
        notify,
        sentChoices,
      );

      if (hasUnknownOutcome(result) || result.results.length < appointmentIds.length) {
        setUncertain(true);
        onUncertainChange(true);
        setUncertainResult(result);
        setError("The cancellation outcome needs checking. Retry this same attempt to check its result; the attempt key is being kept.");
        return;
      }

      const changedAppointmentIds = result.results
        .filter(confirmedRow)
        .map((row) => row.appointment_id);
      const allConfirmed = result.failed_count === 0 &&
        result.results.length === appointmentIds.length &&
        result.results.every((row) => confirmedRow(row));

      if (!allConfirmed) {
        setUncertain(false);
        onUncertainChange(false);
        onFinished(cancellationOutcome(result, false), changedAppointmentIds);
        return;
      }

      try {
        await staffBooking.stopRecurrence(series.recurrence_plan_id);
        setUncertain(false);
        onUncertainChange(false);
        onFinished(cancellationOutcome(result, true), changedAppointmentIds);
      } catch (cause) {
        setUncertain(false);
        onUncertainChange(false);
        const message = cause instanceof ApiError ? cause.message : "The recurring plan could not be stopped.";
        onFinished(cancellationOutcome(result, false, message), changedAppointmentIds);
      }
    } catch (cause) {
      // A 409 is normally uncertain and keeps its key. It is a definite
      // rejection only when a fresh preview now needs a credit choice the
      // request did not carry: the request was refused for what it lacked, and
      // retrying it unchanged could never succeed.
      const staleChoicePreview = cause instanceof ApiError && cause.status === 409
        ? await previewNeedingUnsentChoice(new Set(sentChoices.map((choice) => choice.appointment_id)))
        : null;
      if (staleChoicePreview !== null) {
        key.current = idempotencyKey();
        setUncertain(false);
        onUncertainChange(false);
        setUncertainResult(null);
        setPreview(staleChoicePreview);
        setChoices((current) => keepValidCreditChoices(staleChoicePreview, current));
        setError(`Something changed since the preview: a lesson now needs a credit choice. Choose below, then confirm again. ${(cause as ApiError).message}`);
      } else if (isUncertainFailure(cause)) {
        setUncertain(true);
        onUncertainChange(true);
        setError("The cancellation outcome needs checking. Retry this same attempt to check its result; the attempt key is being kept.");
      } else {
        const next = bookingAttemptAfterFailure(key.current, cause, idempotencyKey);
        key.current = next.key;
        setUncertain(false);
        onUncertainChange(false);
        setUncertainResult(null);
        if (cause instanceof ApiError && cause.status === 409) {
          try {
            const nextPreview = await staffBooking.bulkCancelPreview(appointmentIds, "");
            setPreview(nextPreview);
            setChoices((current) => keepValidCreditChoices(nextPreview, current));
            setError(`Something changed since the preview. ${cause.message}`);
          } catch {
            setPreview(null);
            setError(cause.message);
          }
        } else {
          setPreview(null);
          setError(cause instanceof ApiError ? cause.message : "The series could not be cancelled.");
        }
      }
    } finally {
      setAttemptBusy(false);
    }
  }

  const canConfirm = preview !== null && preview.cancellable_count > 0 &&
    cancellationChoicesComplete(preview, choices);

  return (
    <section className="stack" aria-label="Review series cancellation">
      <div className="row">
        <button type="button" className="quiet small" disabled={busy || uncertain} onClick={onBack}>
          This lesson
        </button>
      </div>
      <h2>Review this and following lessons</h2>
      <ol className="small">
        {series.following.map((item) => (
          <li key={item.amelia_appointment_id}>
            {formatDate(item.starts_at)} at {formatTime(item.starts_at)} · lesson #{item.amelia_appointment_id}
          </li>
        ))}
      </ol>
      {series.truncated && <p className="notice">Only the next 100 lessons are shown.</p>}
      {busy && !preview && <p className="notice small" role="status">Checking cancellation effects…</p>}
      {error && (
        <div className={uncertain ? "notice warn" : "error small"} role="alert">
          {uncertainResult && (
            <ul className="small">
              {uncertainResult.results.map((row) => (
                <li key={row.appointment_id}>
                  Lesson #{row.appointment_id}: {(row as ResultRow).outcome_unknown ? "needs checking" : row.success ? "cancelled" : row.message ?? "needs checking"}
                </li>
              ))}
            </ul>
          )}
          {error}
        </div>
      )}
      {!preview && !busy && error && (
        <button type="button" className="quiet" onClick={() => void look()}>
          Retry cancellation check
        </button>
      )}
      {preview && (
        <BulkCancellationDecisions
          preview={preview}
          busy={busy || uncertain}
          choices={choices}
          onChoice={(appointmentId, resolution) =>
            setChoices((current) => ({ ...current, [appointmentId]: resolution }))
          }
          onChooseAll={(resolution) =>
            setChoices((current) => chooseAllCancellationCredits(preview, current, resolution))
          }
        >
          <label className="stack">
            <span className="small muted">Note (optional)</span>
            <input
              value={reason}
              maxLength={500}
              disabled={busy || uncertain}
              onChange={(event) => setReason(event.currentTarget.value)}
              placeholder="Reason for cancelling these lessons"
            />
          </label>
          <NotifyToggle
            checked={notify}
            onChange={setNotify}
            disabled={busy || uncertain}
            warn
            label="Email the student about each cancelled lesson"
          />
          <div className="row">
            <button
              type="button"
              disabled={busy || (!uncertain && !canConfirm)}
              onClick={() => void confirm()}
            >
              {busy
                ? "Cancelling…"
                : uncertain
                  ? "Retry same cancellation attempt"
                  : `Cancel ${preview.cancellable_count} ${preview.cancellable_count === 1 ? "lesson" : "lessons"} and stop the series`}
            </button>
          </div>
        </BulkCancellationDecisions>
      )}
    </section>
  );
}
